import { useState, useEffect, useMemo } from 'react';
import { Building2, X, ChevronRight, Loader2, Search } from 'lucide-react';
import type { TradingPartnerWithTemplate, InjectedTradingPartner } from '../types/tradingPartner';
import { fetchFilteredTradingPartners, resolveTemplate } from '../utils/tradingPartners';

interface TradingPartnerSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (partner: TradingPartnerWithTemplate) => void;
  documentType?: 'asn' | 'invoice';
  injectedTPs?: InjectedTradingPartner[];
}

export function TradingPartnerSelector({ isOpen, onClose, onSelect, documentType, injectedTPs }: TradingPartnerSelectorProps) {
  const [partners, setPartners] = useState<TradingPartnerWithTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadPartners();
      setSearchQuery('');
    }
  }, [isOpen]);

  const loadPartners = async () => {
    setLoading(true);
    setError(null);
    try {
      // WDEV-50: when the host provides docTypes per TP, scope the list to
      // those supporting the current documentType. A TP with an empty or
      // missing docTypes list is treated as "supports every doc type" —
      // preserves compatibility with pre-WDEV-50 host builds that emit
      // {code, name} only. If documentType is undefined (unusual), skip
      // the filter entirely so we don't silently show an empty picker.
      const scopedTPs = documentType && injectedTPs
        ? injectedTPs.filter(
            (tp) => !tp.docTypes || tp.docTypes.length === 0 || tp.docTypes.includes(documentType),
          )
        : injectedTPs;

      const data = await fetchFilteredTradingPartners(scopedTPs);
      if (scopedTPs && scopedTPs.length > 0 && data.length === 0) {
        setError(
          `None of the ${scopedTPs.length} injected trading partner code(s) matched any active trading partner. ` +
          `Codes attempted: ${scopedTPs.map(tp => tp.code).join(', ')}. ` +
          `Verify that trading_partners.code values match the reports partner IDs.`
        );
        return;
      }
      if (injectedTPs && injectedTPs.length > 0 && scopedTPs && scopedTPs.length === 0) {
        setError(
          `No trading partners for this account support ${documentType?.toUpperCase() || 'this document type'}. ` +
          `Configured TPs (${injectedTPs.length}): ${injectedTPs.map(tp => tp.name).join(', ')}. ` +
          `Add a matching ${documentType === 'asn' ? '856JSON' : documentType === 'invoice' ? '810JSON' : 'JSON'} map for this account.`
        );
        return;
      }
      setPartners(data);
      setSelectedId(null);
    } catch (err) {
      console.error('Failed to load trading partners:', err);
      setError('Failed to load trading partners. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredPartners = useMemo(() => {
    if (!searchQuery.trim()) return partners;
    const q = searchQuery.toLowerCase();
    return partners.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.code.toLowerCase().includes(q) ||
        (p.description && p.description.toLowerCase().includes(q))
    );
  }, [partners, searchQuery]);

  const handleSelect = async (partner: TradingPartnerWithTemplate) => {
    setSelectedId(partner.id);
    try {
      console.log('[TradingPartnerSelector] resolveTemplate:', { partnerId: partner.id, documentType });
      const template = await resolveTemplate(partner.id, documentType || 'asn');
      console.log('[TradingPartnerSelector] resolved template:', template?.id, template?.document_type);
      if (!template || !template.id) {
        setError(`No template found for trading partner "${partner.name}" and document type "${documentType}". Please contact your administrator.`);
        setSelectedId(null);
        return;
      }
      onSelect({
        ...partner,
        template,
      });
    } catch (err) {
      console.error('Failed to load template:', err);
      setError('Failed to load trading partner configuration.');
      setSelectedId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      data-testid="trading-partner-modal"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="text-blue-600" size={24} />
            <h2 className="text-xl font-semibold text-gray-900">Select Trading Partner</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Search bar */}
        {!loading && !error && partners.length > 5 && (
          <div className="px-6 pt-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, code, or description..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {filteredPartners.length} of {partners.length} trading partners
            </p>
          </div>
        )}

        <div className="flex-1 overflow-auto p-6 pt-3">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-blue-600" size={32} />
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
              {error}
            </div>
          )}

          {!loading && !error && partners.length === 0 && (
            <div className="text-center py-12">
              <Building2 className="mx-auto text-gray-400 mb-3" size={48} />
              <p className="text-gray-600">No trading partners available</p>
              <p className="text-sm text-gray-500 mt-1">
                Contact your administrator to set up trading partner configurations
              </p>
            </div>
          )}

          {!loading && !error && partners.length > 0 && (
            <div className="space-y-2">
              {filteredPartners.length === 0 && searchQuery && (
                <div className="text-center py-8">
                  <Search className="mx-auto text-gray-300 mb-2" size={32} />
                  <p className="text-gray-500">No trading partners match "{searchQuery}"</p>
                </div>
              )}
              {filteredPartners.map((partner) => (
                <button
                  key={partner.id}
                  onClick={() => handleSelect(partner)}
                  disabled={selectedId === partner.id}
                  className="w-full text-left p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all group disabled:opacity-50"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-900">{partner.name}</span>
                        <span className="text-xs font-mono bg-gray-100 px-2 py-0.5 rounded text-gray-600">
                          TP-{partner.code}
                        </span>
                      </div>
                      {partner.description && (
                        <p className="text-sm text-gray-600 line-clamp-1">{partner.description}</p>
                      )}
                    </div>
                    {selectedId === partner.id ? (
                      <Loader2 className="animate-spin text-blue-600 flex-shrink-0" size={20} />
                    ) : (
                      <ChevronRight
                        className="text-gray-400 group-hover:text-blue-600 transition-colors flex-shrink-0"
                        size={20}
                      />
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t rounded-b-lg">
          <div className="flex justify-between items-center">
            <p className="text-xs text-gray-500">
              A trading partner with an active template is required to create a document
            </p>
            <button
              onClick={onClose}
              className="btn-secondary px-4 py-2 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
